
import { collection, addDoc, serverTimestamp, query, where, getDocs, doc, updateDoc, increment, Timestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { ActivityType, TrackingLog, User } from '../types';
import { awardXP, XP_ACTIONS } from './gamificationService';

export const logActivity = async (user: User, type: ActivityType, isManual: boolean, metadata: any = {}) => {
  try {
    const logsRef = collection(db, "users", user.id, "activityLogs");
    const now = new Date();
    
    // XP base Logic (Parity)
    let xpAmount = 0;
    switch (type) {
      case 'workout': case 'gym': xpAmount = 50; break;
      case 'diet': xpAmount = 30; break;
      case 'cardio': xpAmount = 40; break;
      case 'yoga': xpAmount = 35; break;
      case 'steps': xpAmount = 25; break;
      default: xpAmount = 10;
    }

    // Check for duplication (Merge Logic)
    // Define 4h window (2h before, 2h after)
    const startTime = new Date(now.getTime() - 2 * 60 * 60 * 1000);
    const endTime = new Date(now.getTime() + 2 * 60 * 60 * 1000);

    // Fixed: Simplified query to avoid requiring a composite index (type + timestamp).
    // We query by the timestamp range only and filter the activity type client-side.
    const q = query(
      logsRef, 
      where("timestamp", ">=", Timestamp.fromDate(startTime))
    );

    let snap;
    try {
      snap = await getDocs(q);
    } catch (error: any) {
      // If permission denied on query, skip duplication check and proceed with creation
      if (error.code === 'permission-denied') {
        console.warn('[TRACKING] Permission denied on activity logs query, proceeding with new log');
        snap = { docs: [] } as any;
      } else {
        throw error;
      }
    }
    
    // Client-side filtering to avoid complex Firestore indexing
    const existingLogs = snap.docs.filter(doc => {
      const data = doc.data();
      const ts = data.timestamp?.toDate();
      return data.type === type && ts && ts <= endTime;
    });
    
    if (existingLogs.length > 0) {
      console.log("Activity duplication detected. Merging data...");
      const existingDoc = existingLogs[0];
      try {
        await updateDoc(doc(db, "users", user.id, "activityLogs", existingDoc.id), {
          "metadata.mergedWithWearable": true,
          ...metadata
        });
        return { status: 'merged', id: existingDoc.id };
      } catch (error: any) {
        if (error.code === 'permission-denied') {
          console.warn('[TRACKING] Permission denied on update, creating new log instead');
          // Fall through to create new log
        } else {
          throw error;
        }
      }
    }

    // Bonus XP for manual log metadata (Optional)
    if (isManual) {
      if (metadata.photoUrl) xpAmount += 10;
      if (metadata.location) xpAmount += 5;
    }

    const newLog = {
      userId: user.id,
      type,
      timestamp: serverTimestamp(),
      xpEarned: xpAmount,
      isManual,
      metadata
    };

    const docRef = await addDoc(logsRef, newLog);
    
    // Award XP (this might also fail, but we don't want to block the log)
    try {
      await awardXP(user.id, xpAmount, `Activity: ${type}`);
    } catch (error: any) {
      console.warn('[TRACKING] Failed to award XP, but activity logged:', error);
    }
    
    return { status: 'success', id: docRef.id, xpEarned: xpAmount };
  } catch (error: any) {
    // Log error but don't throw - we don't want to break the UI
    console.error("Error logging activity:", error);
    // Return a failure status instead of throwing
    return { status: 'error', error: error.message || 'Unknown error' };
  }
};
